import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Save, Plus, Trash2, Loader, CheckCircle, BookOpen, 
  Play, Download, Upload, AlertCircle, Brain
} from 'lucide-react';
import MediaPickerModal from '@/components/MediaPickerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SimpleCourseGenerator() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showUnsplash, setShowUnsplash] = useState(false);
  const [generatingChapterIndex, setGeneratingChapterIndex] = useState(null);
  const [chapterElapsed, setChapterElapsed] = useState(0);
  const [finalReviewElapsed, setFinalReviewElapsed] = useState(0);
  
  // Course metadata
  const [courseTitle, setCourseTitle] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [category, setCategory] = useState('');
  const [tier, setTier] = useState('basic');
  
  // Chapters (max 5)
  const [chapters, setChapters] = useState([
    { number: 1, title: '', content: '', generated: false, materials: null }
  ]);
  
  // Final Review
  const [finalReviewGenerated, setFinalReviewGenerated] = useState(false);
  const [finalReviewMaterials, setFinalReviewMaterials] = useState(null);
  
  // Draft management
  const [savedSessions, setSavedSessions] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/simple-course/sessions`, {
        withCredentials: true
      });
      setSavedSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const addChapter = () => {
    if (chapters.length >= 5) {
      toast.error('Maximum 5 chapters allowed');
      return;
    }
    setChapters([...chapters, {
      number: chapters.length + 1,
      title: '',
      content: '',
      generated: false,
      materials: null
    }]);
  };

  const removeChapter = (index) => {
    if (chapters.length === 1) {
      toast.error('Must have at least one chapter');
      return;
    }
    const newChapters = chapters.filter((_, i) => i !== index);
    // Renumber chapters
    newChapters.forEach((ch, idx) => ch.number = idx + 1);
    setChapters(newChapters);
  };

  const updateChapter = (index, field, value) => {
    const newChapters = [...chapters];
    newChapters[index][field] = value;
    setChapters(newChapters);
  };

  const generateChapter = async (index) => {
    const chapter = chapters[index];
    
    if (!chapter.title || !chapter.content) {
      toast.error('Please provide chapter title and content');
      return;
    }

    setGeneratingChapterIndex(index);
    setChapterElapsed(0);
    setLoading(true);

    const timerRef = setInterval(() => {
      setChapterElapsed(prev => prev + 1);
    }, 1000);

    try {
      // Step 1: Start the background job (returns immediately — no Cloudflare timeout)
      const startRes = await axios.post(
        `${API}/admin/simple-course/generate-chapter`,
        {
          title: chapter.title,
          content: chapter.content,
          chapter_number: chapter.number,
          course_id: 'temp_course'
        },
        { withCredentials: true, timeout: 15000 }
      );
      const { job_id } = startRes.data;

      // Step 2: Poll for completion. Tolerate a few transient network/timeout hiccups
      // in a row instead of failing the whole flow on one blip.
      let attempts = 0;
      let consecutiveFailures = 0;
      const maxAttempts = 60; // 3-min max (60 * 3s)
      const maxConsecutiveFailures = 5;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const statusRes = await axios.get(
            `${API}/admin/simple-course/generate-chapter/status/${job_id}`,
            { withCredentials: true, timeout: 15000 }
          );
          consecutiveFailures = 0;
          const job = statusRes.data;
          if (job.status === 'done') {
            const newChapters = [...chapters];
            newChapters[index].generated = true;
            newChapters[index].materials = job.materials;
            setChapters(newChapters);
            toast.success(`Chapter ${chapter.number} generated — 7 study materials created!`);
            return;
          } else if (job.status === 'error') {
            throw new Error(job.error || 'Generation failed on server');
          }
        } catch (pollError) {
          if (pollError.message && pollError.message !== 'Network Error' && !pollError.code) {
            throw pollError;
          }
          consecutiveFailures++;
          if (consecutiveFailures >= maxConsecutiveFailures) {
            throw new Error('Lost connection while checking generation status. The chapter may still finish on the server — try reloading or saving the draft in a moment.');
          }
        }
        attempts++;
      }
      throw new Error('Generation timed out after 3 minutes. Please try again.');
    } catch (error) {
      console.error('Chapter generation failed:', error);
      const msg = error.code === 'ECONNABORTED'
        ? 'Connection timed out. The server is still generating — please wait a moment and try saving the draft.'
        : (error.response?.data?.detail || error.message || 'Failed to generate chapter. Please try again.');
      toast.error(msg, { duration: 8000 });
    } finally {
      clearInterval(timerRef);
      setLoading(false);
      setGeneratingChapterIndex(null);
      setChapterElapsed(0);
    }
  };

  const generateFinalReview = async () => {
    // Check all chapters are generated
    const allGenerated = chapters.every(ch => ch.generated);
    if (!allGenerated) {
      toast.error('Please generate all chapters first');
      return;
    }

    setLoading(true);
    setFinalReviewElapsed(0);
    const timerRef = setInterval(() => {
      setFinalReviewElapsed(prev => prev + 1);
    }, 1000);

    try {
      // Combine all chapter content
      const allContent = chapters.map(ch => 
        `Chapter ${ch.number}: ${ch.title}\n\n${ch.content}`
      ).join('\n\n---\n\n');

      // Step 1: Start background job (returns immediately — no Cloudflare timeout)
      const startRes = await axios.post(
        `${API}/admin/simple-course/generate-final-review`,
        {
          all_chapters_content: allContent,
          course_id: 'temp_course',
          course_title: courseTitle
        },
        { withCredentials: true, timeout: 15000 }
      );
      const { job_id } = startRes.data;

      // Step 2: Poll for completion. Tolerate a few transient network/timeout hiccups.
      let attempts = 0;
      let consecutiveFailures = 0;
      const maxAttempts = 60; // 3-min max (60 * 3s)
      const maxConsecutiveFailures = 5;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const statusRes = await axios.get(
            `${API}/admin/simple-course/generate-final-review/status/${job_id}`,
            { withCredentials: true, timeout: 15000 }
          );
          consecutiveFailures = 0;
          const job = statusRes.data;
          if (job.status === 'done') {
            setFinalReviewGenerated(true);
            setFinalReviewMaterials(job.materials);
            toast.success('Final Review generated successfully!');
            return;
          } else if (job.status === 'error') {
            throw new Error(job.error || 'Final review generation failed on server');
          }
        } catch (pollError) {
          if (pollError.message && pollError.message !== 'Network Error' && !pollError.code) {
            throw pollError;
          }
          consecutiveFailures++;
          if (consecutiveFailures >= maxConsecutiveFailures) {
            throw new Error('Lost connection while checking generation status. Try reloading in a moment.');
          }
        }
        attempts++;
      }
      throw new Error('Final review generation timed out after 3 minutes. Please try again.');
    } catch (error) {
      console.error('Final review generation failed:', error);
      const msg = error.code === 'ECONNABORTED'
        ? 'Connection timed out. Please try again in a moment.'
        : (error.response?.data?.detail || error.message || 'Failed to generate final review');
      toast.error(msg, { duration: 8000 });
    } finally {
      clearInterval(timerRef);
      setLoading(false);
      setFinalReviewElapsed(0);
    }
  };

  const saveDraft = async (slot) => {
    if (!courseTitle) {
      toast.error('Please provide a course title');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/simple-course/sessions`,
        {
          slot_number: slot,
          course_title: courseTitle,
          course_description: courseDescription,
          cover_image_url: coverImageUrl,
          category: category,
          tier: tier,
          chapters: chapters.map(ch => ({
            chapter_number: ch.number,
            title: ch.title,
            content: ch.content,
            generated: ch.generated,
            ...ch.materials
          })),
          final_review_generated: finalReviewGenerated,
          final_review_chapter_id: finalReviewMaterials?.chapter_id,
          final_exam_id: finalReviewMaterials?.final_exam_id
        },
        { withCredentials: true }
      );

      toast.success(`Draft saved to slot ${slot}`);
      loadSessions();
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft');
    }
  };

  const loadDraft = async (slot) => {
    const session = savedSessions.find(s => s.slot_number === slot);
    if (!session) return;

    setCourseTitle(session.course_title);
    setCourseDescription(session.course_description);
    setCoverImageUrl(session.cover_image_url || '');
    setCategory(session.category || '');
    setTier(session.tier || 'basic');
    setChapters(session.chapters.map(ch => ({
      number: ch.chapter_number,
      title: ch.title,
      content: ch.content,
      generated: ch.generated,
      materials: ch.generated ? {
        chapter_id: ch.chapter_id,
        study_guide_id: ch.study_guide_id,
        flashcard_id: ch.flashcard_id,
        matching_id: ch.matching_id,
        memory_id: ch.memory_id,
        fill_in_id: ch.fill_in_id,
        jeopardy_id: ch.jeopardy_id,
        quiz_id: ch.quiz_id
      } : null
    })));
    setFinalReviewGenerated(session.final_review_generated);
    setFinalReviewMaterials(session.final_review_generated ? {
      chapter_id: session.final_review_chapter_id,
      final_exam_id: session.final_exam_id
    } : null);
    setSelectedSlot(slot);

    toast.success(`Loaded draft from slot ${slot}`);
  };

  const deleteDraft = async (slot) => {
    try {
      await axios.delete(`${API}/admin/simple-course/sessions/${slot}`, {
        withCredentials: true
      });
      toast.success(`Slot ${slot} cleared`);
      loadSessions();
      if (selectedSlot === slot) {
        setSelectedSlot(null);
      }
    } catch (error) {
      console.error('Failed to delete draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  const completeCourse = async () => {
    if (!courseTitle || !courseDescription) {
      toast.error('Please provide course title and description');
      return;
    }

    const allGenerated = chapters.every(ch => ch.generated);
    if (!allGenerated || !finalReviewGenerated) {
      toast.error('Please generate all chapters and final review');
      return;
    }

    setLoading(true);
    try {
      // Prepare course data
      const chapterIds = chapters.map(ch => ch.materials.chapter_id);
      chapterIds.push(finalReviewMaterials.chapter_id); // Add final review chapter

      const chapterMaterials = chapters.map(ch => ch.materials);
      chapterMaterials.push(finalReviewMaterials); // Add final review materials

      const response = await axios.post(
        `${API}/admin/simple-course/complete`,
        {
          title: courseTitle,
          description: courseDescription,
          cover_image_url: coverImageUrl,
          category: category,
          tier: tier,
          chapter_ids: chapterIds,
          chapter_materials: chapterMaterials,
          final_exam_id: finalReviewMaterials.final_exam_id
        },
        { withCredentials: true }
      );

      toast.success('Course created successfully!');
      
      // Clear draft if one was loaded
      if (selectedSlot) {
        await deleteDraft(selectedSlot);
      }
      
      // Redirect to courses admin
      setTimeout(() => {
        navigate('/admin?tab=courses');
      }, 2000);

    } catch (error) {
      console.error('Failed to complete course:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#2C1810] to-[#3D2817]">
        <div className="max-w-5xl mx-auto">
          
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-5xl retro-title mb-4">Simple Course Generator</h1>
            <p className="text-[#F4E4C1] text-lg">Create courses chapter by chapter with AI-generated study materials</p>
          </div>

          {/* Draft Management */}
          <div className="cassette-card p-6 mb-8">
            <h2 className="text-2xl retro-title text-[#2C1810] mb-4">Save Slots (Max 5)</h2>
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map(slot => {
                const session = savedSessions.find(s => s.slot_number === slot);
                return (
                  <div key={slot} className="relative">
                    <button
                      onClick={() => session ? loadDraft(slot) : saveDraft(slot)}
                      className={`w-full p-3 rounded border-2 transition-all ${
                        session
                          ? 'border-[#4D9E7C] bg-[#4D9E7C]/10 hover:bg-[#4D9E7C]/20'
                          : 'border-[#E8934D] bg-[#E8934D]/10 hover:bg-[#E8934D]/20'
                      }`}
                    >
                      <div className="text-center">
                        {session ? <Download className="w-5 h-5 mx-auto mb-1" /> : <Save className="w-5 h-5 mx-auto mb-1" />}
                        <p className="text-xs font-bold">Slot {slot}</p>
                        {session && <p className="text-xs truncate">{session.course_title}</p>}
                      </div>
                    </button>
                    {session && (
                      <button
                        onClick={() => deleteDraft(slot)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Course Metadata */}
          <div className="cassette-card p-8 mb-8">
            <h2 className="text-2xl retro-title text-[#2C1810] mb-6">Course Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[#2C1810] font-bold mb-2">Course Title *</label>
                <input
                  type="text"
                  value={courseTitle}
                  onChange={(e) => setCourseTitle(e.target.value)}
                  placeholder="Enter course title"
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white"
                />
              </div>

              <div>
                <label className="block text-[#2C1810] font-bold mb-2">Description *</label>
                <textarea
                  value={courseDescription}
                  onChange={(e) => setCourseDescription(e.target.value)}
                  placeholder="Enter course description"
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Category</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g., Acupuncture"
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white"
                  />
                </div>

                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Tier</label>
                  <select
                    value={tier}
                    onChange={(e) => setTier(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white"
                  >
                    <option value="basic">Basic</option>
                    <option value="comps_ready">Comps Ready</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[#2C1810] font-bold mb-2">Cover Image</label>
                <button
                  type="button"
                  onClick={() => setShowUnsplash(true)}
                  className="retro-button mb-2"
                >
                  Choose Image
                </button>
                {coverImageUrl && (
                  <div className="mt-2 relative">
                    <img src={coverImageUrl} alt="Cover" className="w-full h-48 object-cover rounded" />
                    <button
                      onClick={() => setCoverImageUrl('')}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chapters */}
          <div className="space-y-6 mb-8">
            {chapters.map((chapter, index) => (
              <div key={index} className="admin-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl retro-title">Chapter {chapter.number}</h3>
                  <div className="flex gap-2">
                    {chapter.generated && (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    )}
                    {chapters.length > 1 && (
                      <button
                        onClick={() => removeChapter(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-[#F4E4C1] font-bold mb-2">Chapter Title *</label>
                    <input
                      type="text"
                      value={chapter.title}
                      onChange={(e) => updateChapter(index, 'title', e.target.value)}
                      placeholder="Enter chapter title"
                      disabled={chapter.generated}
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-[#F4E4C1] font-bold mb-2">Chapter Content *</label>
                    <textarea
                      value={chapter.content}
                      onChange={(e) => updateChapter(index, 'content', e.target.value)}
                      placeholder="Paste or type chapter content here..."
                      rows={8}
                      disabled={chapter.generated}
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded"
                    />
                  </div>

                  {!chapter.generated ? (
                    <div>
                      {generatingChapterIndex === index ? (
                        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <Loader className="w-6 h-6 text-amber-600 animate-spin flex-shrink-0" />
                            <div>
                              <p className="font-bold text-amber-800">
                                Generating Chapter {chapter.number}... ({chapterElapsed}s)
                              </p>
                              <p className="text-sm text-amber-700">
                                Creating 7 study materials. This takes 30–60 seconds — please wait.
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-7 gap-1 mt-2">
                            {['Study Guide','Flashcards','Matching','Memory','Fill-In','Jeopardy','Test'].map((m, i) => (
                              <div key={m} className="text-center">
                                <div className={`w-full h-2 rounded-full mb-1 ${chapterElapsed > i * 8 ? 'bg-amber-500 animate-pulse' : 'bg-amber-200'}`} />
                                <p className="text-xs text-amber-700 truncate">{m}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => generateChapter(index)}
                          disabled={loading}
                          className="retro-button w-full flex items-center justify-center gap-2"
                          data-testid={`generate-chapter-btn-${index}`}
                        >
                          <Play className="w-5 h-5" />
                          Generate Chapter {chapter.number}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="bg-green-50 border-2 border-green-600 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-6 h-6 text-green-700 flex-shrink-0" />
                        <p className="text-green-800 font-bold">Chapter {chapter.number} Generated Successfully!</p>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {['Study Guide','Flashcards','Matching','Memory','Fill-In','Jeopardy','Test'].map((m) => (
                          <div key={m} className="text-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mx-auto mb-1" />
                            <p className="text-xs text-green-700 truncate">{m}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add Chapter Button */}
          {chapters.length < 5 && (
            <button
              onClick={addChapter}
              className="retro-button w-full mb-8 flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Chapter ({chapters.length}/5)
            </button>
          )}

          {/* Final Review */}
          <div className="admin-card p-6 mb-8">
            <h3 className="text-2xl retro-title mb-4 flex items-center gap-2">
              <Brain className="w-7 h-7" />
              Final Review & Comprehensive Exam
            </h3>
            
            {!finalReviewGenerated ? (
              <div>
                <p className="text-[#F4E4C1] mb-4">
                  Generate a comprehensive final review chapter covering all previous chapters, plus a 30-question final exam.
                </p>
                <button
                  onClick={generateFinalReview}
                  disabled={loading || !chapters.every(ch => ch.generated)}
                  className="retro-button w-full flex items-center justify-center gap-2"
                  data-testid="generate-final-review-btn"
                >
                  {loading && generatingChapterIndex === null ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Generating Final Review... ({finalReviewElapsed}s)
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      Generate Final Review
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="bg-green-100 border-2 border-green-600 rounded p-4">
                <p className="text-green-800 font-bold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Final Review Generated! (7 Study Materials + 30-Question Final Exam)
                </p>
              </div>
            )}
          </div>

          {/* Complete Course */}
          <div className="cassette-card p-6">
            <h3 className="text-2xl retro-title text-[#2C1810] mb-4">Complete Course</h3>
            <p className="text-[#6B4423] mb-4">
              Once all chapters and final review are generated, complete the course to add it to your courses list.
            </p>
            <button
              onClick={completeCourse}
              disabled={loading || !finalReviewGenerated}
              className="retro-button w-full flex items-center justify-center gap-2 text-lg py-4"
            >
              {loading ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  Completing Course...
                </>
              ) : (
                <>
                  <BookOpen className="w-6 h-6" />
                  Complete & Save Course
                </>
              )}
            </button>
          </div>

        </div>
      </div>

      {showUnsplash && (
        <MediaPickerModal
          isOpen={showUnsplash}
          onSelect={(url) => {
            setCoverImageUrl(url);
            setShowUnsplash(false);
          }}
          onClose={() => setShowUnsplash(false)}
        />
      )}
    </Layout>
  );
}
